import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, UserMinus, Phone, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useTalk, TkAvatar, tkErr } from "./TalkLayout";
import { MemberPicker, Sheet } from "./MemberPicker";

export function GroupCreate({ onClose }) {
  const [name, setName] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/talk/groups", { name: name.trim(), member_ids: sel.map((u) => u.user_id) }); nav(`/talk/c/${data.chat.id}`); }
    catch (e) { tkErr(e); setBusy(false); }
  };
  return (
    <Sheet title="New group" onClose={onClose} testId="talk-group-create">
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={50} placeholder="Group name" data-testid="talk-group-name-input"
        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 outline-none focus:border-[#FF2BD6] mb-4" />
      <MemberPicker selected={sel} setSelected={setSel} />
      <div className="text-[11px] text-white/40 mt-2">Up to 50 members. Anyone in the group can add or remove people.</div>
      <button onClick={create} disabled={busy || !name.trim() || !sel.length} data-testid="talk-group-create-submit" className="mt-4 w-full bg-[#FF2BD6] text-black font-black rounded-full py-3 disabled:opacity-40">{busy ? "Creating…" : `Create group (${sel.length + 1})`}</button>
    </Sheet>
  );
}

export function GroupInfo({ chat, onClose, onChange }) {
  const { me, engine } = useTalk();
  const [adding, setAdding] = useState([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [name, setName] = useState(chat.name);
  const [shared, setShared] = useState([]);
  const nav = useNavigate();
  const uid = me.profile.user_id;
  useEffect(() => { api.get(`/talk/chats/${chat.id}/shared`).then((r) => setShared(r.data.items)).catch(() => {}); }, [chat.id]);
  const add = async () => {
    try { const { data } = await api.post(`/talk/groups/${chat.id}/members`, { user_ids: adding.map((u) => u.user_id) }); setAdding([]); setPickerKey((k) => k + 1); onChange(data.chat); toast.success("Members added"); } catch (e) { tkErr(e); }
  };
  const remove = async (u) => {
    const self = u.user_id === uid;
    if (!window.confirm(self ? `Leave ${chat.name}?` : `Remove ${u.display_name} from the group?`)) return;
    try {
      await api.delete(`/talk/groups/${chat.id}/members/${u.user_id}`);
      if (self) { if (engine?.group?.chat_id === chat.id) engine.leaveGroup(); nav("/talk"); }
      else onChange({ ...chat, members: chat.members.filter((m) => m.user_id !== u.user_id) });
    } catch (e) { tkErr(e); }
  };
  const rename = async () => { try { const { data } = await api.patch(`/talk/groups/${chat.id}`, { name }); onChange(data.chat); toast("Group renamed"); } catch (e) { tkErr(e); } };
  return (
    <Sheet title="Group info" onClose={onClose} testId="talk-group-info">
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={50} className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 outline-none" data-testid="talk-group-rename-input" />
        <button onClick={rename} disabled={!name.trim() || name === chat.name} data-testid="talk-group-rename-btn" className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-30" aria-label="Rename"><Pencil className="w-4 h-4" /></button>
      </div>
      <button onClick={() => { onClose(); engine?.joinGroup(chat); }} data-testid="talk-group-info-call" className="mt-3 w-full rounded-full bg-[#39FF14] text-black font-black py-2.5 flex items-center justify-center gap-2"><Phone className="w-4 h-4" />{chat.call ? `Join call (${chat.call.peers.length})` : "Start group voice call"}</button>
      {shared.length > 0 && (
        <div className="mt-5">
          <div className="mono text-[10px] uppercase tracking-widest text-white/40 mb-2">Shared photos & videos · {shared.length}</div>
          <div className="grid grid-cols-4 gap-1.5" data-testid="talk-group-shared">
            {shared.slice(0, 24).map((s) => (
              <a key={s.id} href={s.media.url} target="_blank" rel="noreferrer" className="aspect-square rounded-xl overflow-hidden bg-white/5">
                {s.kind === "image" ? <img src={s.media.url} alt="" className="w-full h-full object-cover" /> : <video src={s.media.url} className="w-full h-full object-cover" muted />}
              </a>
            ))}
          </div>
        </div>
      )}
      <div className="mono text-[10px] uppercase tracking-widest text-white/40 mt-5 mb-2">Members · {chat.members.length}/50</div>
      <div className="space-y-1" data-testid="talk-group-members">
        {chat.members.map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 p-1.5" data-testid={`talk-group-member-${m.username}`}>
            <TkAvatar p={m} size={36} />
            <div className="flex-1 min-w-0"><div className="font-bold text-sm truncate">{m.display_name}{m.user_id === uid && " (you)"}</div><div className="text-[11px] mono text-white/50">@{m.username}</div></div>
            {m.user_id !== uid && <button onClick={() => remove(m)} data-testid={`talk-group-remove-${m.username}`} aria-label="Remove" className="text-white/40 hover:text-[#FF003C]"><UserMinus className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <div className="mono text-[10px] uppercase tracking-widest text-white/40 mt-5 mb-2">Add people</div>
      <MemberPicker key={pickerKey} selected={adding} setSelected={setAdding} exclude={chat.members.map((m) => m.user_id)} />
      {adding.length > 0 && <button onClick={add} data-testid="talk-group-add-submit" className="mt-3 w-full bg-[#FF2BD6] text-black font-black rounded-full py-2.5">Add {adding.length}</button>}
      <button onClick={() => remove(me.profile)} data-testid="talk-group-leave" className="mt-6 w-full text-sm text-[#FF003C] flex items-center justify-center gap-2"><LogOut className="w-4 h-4" /> Leave group</button>
    </Sheet>
  );
}
